import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LaunchIcon from '@mui/icons-material/Launch';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import DatePicker from '@mui/lab/DatePicker';
import useForm from '../../../../components/_hook/useForm';
import useLanguage from '../../../../components/_hook/useLanguage';
import { useUpdateProductInventoryMutation } from '../../../../../graphql/mutation/product/updateProductInventoryMutation';
import useNotification from '../../../../components/_hook/useNotification';
import useRouter from '../../../../components/_hook/useRouter';
import { useProductQuery } from '../../../../../graphql/query/product/useProductQuery';
import NotFound from '../../../../components/_error/NotFound';
import Context from '../../../../context';
import useRoutes from '../../../../components/_hook/useRoutes';
import ProductLayout from '../../../../layout/Product';
import StickyFormFooter from '../../../../components/_form/StickyFormFooter';
import useKeepitalRoutes from '../../../../components/_hook/useKeepitalRoutes';
import Helmet from '../../../../components/Helmet';
import Select, { IValue } from '../../../../components/_select/_module/Select';
import ProductDetailDropshippedProductInfo from '../_module/ProductDetailDropshippedProductInfo';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
  },
}));

function ProductDetailInventory() {
  interface IFormValue {
    notificationLowStock: number;
    notificationNoStock: number;
    stockInStock: number;
    stockReserved: number;
    stockLeadTime: number;
    supplyAbilityNumber: number;
    supplyAbilityTime: string;
    availability: string;
    availabilityDateFrom: Date | null;
    availabilityDateTo: Date | null;
  }

  const { t } = useTranslation();
  const classes = useStyles();
  const { notification } = useNotification();
  const { params, history } = useRouter();
  const { path } = useRoutes();
  const { company } = useContext(Context);
  const { docID: productId } = params;
  const { getText } = useLanguage();
  const { path: keepitalPath } = useKeepitalRoutes();
  const productFragment = `
            _id
            status
            slug
            featured
            name {
              la
              text
            }
            isDropshippedProduct
            inventory {
              notification {
                low_stock
                no_stock
              }
              stock {
                in_stock
                reserved
                lead_time
              }
              supply_ability {
                number
                time
              }
              availability
              availability_date {
                from
                to
              }
            }
  `;
  const { data, loading } = useProductQuery(productFragment, {
    variables: {
      filter: {
        companyId: company._id,
        _id: productId as string,
      },
    },
    skip: productId === 'new',
  });

  function onCompleted() {
    notification.success(t('done'));
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateProductInventoryMutation,
    { loading: isUpdatingProduct },
  ] = useUpdateProductInventoryMutation(
    `updatedProduct {
				${productFragment}
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(tempValues: IFormValue) {
    if (data) {
      await updateProductInventoryMutation({
        variables: {
          _id: data.products.items[0]._id,
          notificationLowStock: tempValues.notificationLowStock,
          notificationNoStock: tempValues.notificationNoStock,
          stockInStock: tempValues.stockInStock,
          stockReserved: tempValues.stockReserved,
          stockLeadTime: tempValues.stockLeadTime,
          supplyAbilityNumber: tempValues.supplyAbilityNumber,
          supplyAbilityTime: parseInt(tempValues.supplyAbilityTime, 10),
          availability: parseInt(tempValues.availability, 10),
          availabilityDateFrom: tempValues.availabilityDateFrom || undefined,
          availabilityDateTo: tempValues.availabilityDateTo || undefined,
        },
      });
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    setValues,
    errors,
  } = useForm<IFormValue>({
    defaultValues: {
      notificationLowStock: 0,
      notificationNoStock: 0,
      stockInStock: 0,
      stockReserved: 0,
      stockLeadTime: 0,
      supplyAbilityNumber: 0,
      supplyAbilityTime: '',
      availability: '',
      availabilityDateFrom: null,
      availabilityDateTo: null,
    },
    onSubmit: onSubmitForm,
  });

  useEffect(() => {
    if (!loading && data && data.products.items.length) {
      const product = data.products.items[0];
      const productInventory = product.inventory || {};
      const productInventoryNotification = productInventory.notification || {};
      const productInventoryStock = productInventory.stock || {};
      const productInventorySupplyAbility =
        productInventory.supply_ability || {};
      const productInventoryAvailabilityDate =
        productInventory.availability_date || {};

      setValues({
        notificationLowStock: productInventoryNotification.low_stock
          ? productInventoryNotification.low_stock
          : 0,
        notificationNoStock: productInventoryNotification.no_stock
          ? productInventoryNotification.no_stock
          : 0,
        stockInStock: productInventoryStock.in_stock
          ? productInventoryStock.in_stock
          : 0,
        stockReserved: productInventoryStock.reserved
          ? productInventoryStock.reserved
          : 0,
        stockLeadTime: productInventoryStock.lead_time
          ? productInventoryStock.lead_time
          : 0,
        supplyAbilityNumber: productInventorySupplyAbility.number
          ? productInventorySupplyAbility.number
          : 0,
        supplyAbilityTime: productInventorySupplyAbility.time
          ? productInventorySupplyAbility.time.toString()
          : '',
        availability: productInventory.availability
          ? productInventory.availability.toString()
          : '',
        availabilityDateFrom: productInventoryAvailabilityDate.from
          ? productInventoryAvailabilityDate.from
          : null,
        availabilityDateTo: productInventoryAvailabilityDate.to
          ? productInventoryAvailabilityDate.to
          : null,
      });
    }
  }, [loading]);

  if (productId !== 'new' && loading && productId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  const product = productId && data ? data.products.items[0] : null;

  if (!product) {
    return <NotFound />;
  }

  if (product && product.isDropshippedProduct) {
    return <ProductDetailDropshippedProductInfo />;
  }

  return (
    <>
      <Paper square className={classes.paper}>
        <Helmet title={`${t('inventory')} | ${t('product')}`} />
        <Grid container spacing={2}>
          <Grid container justifyContent="space-between" spacing={1}>
            <Grid item>
              <Typography variant="h6">
                {t('update')} {getText(product.name)} {t('inventory')}
              </Typography>
            </Grid>
            {product && (
              <Grid item>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  startIcon={<LaunchIcon />}
                  component="a"
                  href={keepitalPath('company.showcase.product.index', {
                    origin: company.origin ? company.origin.toLowerCase() : '',
                    companySlug: company.slug ? company.slug : '',
                    productSlug: product.slug,
                  })}
                  target="_blank"
                >
                  {t('view')}
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={3}>
            <Grid container item xs={12} spacing={1}>
              <Grid item xs={12}>
                <Typography variant="h6">{t('inventory')}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('in_stock')}
                  value={values.stockInStock}
                  onChange={(e) =>
                    setValue('stockInStock', parseInt(e.target.value, 10))
                  }
                  error={Boolean(errors.stockInStock)}
                  helperText={errors.stockInStock}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('reserved')}
                  value={values.stockReserved}
                  onChange={(e) =>
                    setValue('stockReserved', parseInt(e.target.value, 10))
                  }
                  error={Boolean(errors.stockReserved)}
                  helperText={errors.stockReserved}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('lead_time (day)')}
                  value={values.stockLeadTime}
                  onChange={(e) =>
                    setValue('stockLeadTime', parseInt(e.target.value, 10))
                  }
                  error={Boolean(errors.stockLeadTime)}
                  helperText={errors.stockLeadTime}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Paper square className={classes.paper}>
        <Grid container spacing={2}>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">{t('availability')}</Typography>
            </Grid>
            <Grid item xs={12} container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  {t('supply_ability')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('supply_ability')}
                  value={values.supplyAbilityNumber}
                  onChange={(e) =>
                    setValue(
                      'supplyAbilityNumber',
                      parseInt(e.target.value, 10)
                    )
                  }
                  error={Boolean(errors.supplyAbilityNumber)}
                  helperText={errors.supplyAbilityNumber}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{t('per')}</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select<IValue>
                  fullWidth
                  type="number"
                  label={t('supply_ability')}
                  value={values.supplyAbilityTime}
                  options={[
                    {
                      label: t('millisecond'),
                      value: '0',
                    },
                    {
                      label: t('second'),
                      value: '1',
                    },
                    {
                      label: t('minute'),
                      value: '2',
                    },
                    {
                      label: t('hour'),
                      value: '3',
                    },
                    {
                      label: t('day'),
                      value: '4',
                    },
                    {
                      label: t('week'),
                      value: '5',
                    },
                    {
                      label: t('month'),
                      value: '6',
                    },
                    {
                      label: t('year'),
                      value: '7',
                    },
                  ]}
                  onChange={(option) =>
                    setValue('supplyAbilityTime', option ? option.value : '')
                  }
                  error={Boolean(errors.supplyAbilityTime)}
                  helperText={errors.supplyAbilityTime}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  {t('current_availability')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Select<IValue>
                  options={[
                    {
                      value: '1',
                      label: t('in_stock'),
                    },
                    {
                      value: '2',
                      label: t('limited_stock'),
                    },
                    {
                      value: '3',
                      label: t('24_hours'),
                    },
                    {
                      value: '4',
                      label: t('48_hours'),
                    },
                    {
                      value: '5',
                      label: t('2_3_days'),
                    },
                    {
                      value: '6',
                      label: t('3_5_days'),
                    },
                    {
                      value: '7',
                      label: t('7_days'),
                    },
                    {
                      value: '8',
                      label: t('14_days'),
                    },
                    {
                      value: '9',
                      label: t('1_4_weeks'),
                    },
                    {
                      value: '10',
                      label: t('1_2_months'),
                    },
                    {
                      value: '11',
                      label: t('more_then_3_months'),
                    },
                    {
                      value: '12',
                      label: t('sold_out'),
                    },
                    {
                      value: '13',
                      label: t('pre_order'),
                    },
                    {
                      value: '14',
                      label: t('in_store_only'),
                    },
                    {
                      value: '15',
                      label: t('online_only'),
                    },
                    {
                      value: '16',
                      label: t('out_of_stock'),
                    },
                    {
                      value: '17',
                      label: t('discontinued'),
                    },
                  ]}
                  fullWidth
                  type="number"
                  label={t('current_availability')}
                  value={values.availability}
                  onChange={(option) =>
                    setValue('availability', option ? option.value : '')
                  }
                  error={Boolean(errors.availability)}
                  helperText={errors.availability}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <DatePicker
                    inputFormat="dd/MMM/yyyy"
                    renderInput={(tempProps) => (
                      <TextField
                        {...tempProps}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {t('from')}
                            </InputAdornment>
                          ),
                          ...tempProps.InputProps,
                        }}
                      />
                    )}
                    label={t('date_available')}
                    value={values.availabilityDateFrom || null}
                    onChange={(date) => {
                      setValue('availabilityDateFrom', date || null);
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <DatePicker
                    inputFormat="dd/MMM/yyyy"
                    renderInput={(tempProps) => (
                      <TextField
                        {...tempProps}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {t('to')}
                            </InputAdornment>
                          ),
                          ...tempProps.InputProps,
                        }}
                      />
                    )}
                    label={t('date_available')}
                    value={values.availabilityDateTo || null}
                    onChange={(date) => {
                      setValue('availabilityDateTo', date || null);
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Paper square className={classes.paper}>
        <Grid container spacing={2}>
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h6">{t('notification')}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label={t('low_stock')}
                value={values.notificationLowStock}
                onChange={(e) =>
                  setValue('notificationLowStock', parseInt(e.target.value, 10))
                }
                error={Boolean(errors.notificationLowStock)}
                helperText={errors.notificationLowStock}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label={t('no_stock')}
                value={values.notificationNoStock}
                onChange={(e) =>
                  setValue('notificationNoStock', parseInt(e.target.value, 10))
                }
                error={Boolean(errors.notificationNoStock)}
                helperText={errors.notificationNoStock}
              />
            </Grid>
          </Grid>
          <StickyFormFooter
            onCancel={() =>
              history.push(
                path('product.list', {
                  companyID: company._id,
                })
              )
            }
            onSubmit={handleSubmit}
            isSubmitting={isUpdatingProduct}
          />
        </Grid>
      </Paper>
    </>
  );
}

export default () => <ProductLayout main={<ProductDetailInventory />} />;
